import type { ApiClient } from '@acme/api-client'
import type { SavedTableLayout } from '@acme/app-shell'
import type { TableLayout } from '@acme/ui'
import { inject, type InjectionKey } from 'vue'
import type {
  AccessPreview,
  DataPartitions,
  AuditChangePage,
  AuditQueryPage,
  AskAssistantResponse,
  ActionDraftView,
  ActionPlanProposal,
  AssistantProfile,
  Conversation,
  ConversationSummary,
  ConversationDetail,
  ConversationListPage,
  UnidentifiedContact,
  AuditChangeItem,
  ErpTestResult,
  NotificationList,
  UpdateTenantRole,
  UpdateRoleResult,
  AccessPreviewQuery,
  ChangeUserAccessCommand,
  ChangeUserContactsCommand,
  ChangeUserContactsResponse,
  DomainSettings,
  EmailSettings,
  InvitationResult,
  InviteUserCommand,
  InviteUserResponse,
  Me,
  Tenant,
  TenantDomain,
  TenantRole,
  TenantUser,
  TenantUserFilter,
  TenantUserList,
  UpdateEmailSettings,
  UpdateTenantSettings,
  TenantTheme,
  UpdateTenantTheme,
  SuggestThemeColors,
  ThemeColorsSuggestion,
  AssistantSettings,
  AssistantUpdate,
  SharedWhatsApp,
  UpdateWhatsAppConnection,
  WhatsAppConnection,
  WhatsAppQrCode,
  WhatsAppTest,
  AiSettings,
  AiCredentialSaved,
  AiKeyCheck,
  AiProfile,
  SaveAiProfile,
  UpdateAiDefaults,
} from './types'

// Assistente IA API (assistente-ia/src/AssistenteIA.Api). Every call takes an AbortSignal so
// leaving a screen cancels its pending requests.
// The images of Configurações › Marca (?variant= of the logo endpoints; the normal logo has none).
export type LogoVariant = 'light' | 'dark' | 'favicon' | 'icon' | 'icon-dark'
const variantQuery = (variant: LogoVariant, prefix: string, suffix = '') => (variant === 'light' ? '' : `${prefix}variant=${variant}${suffix}`)

export function createPortalApi(client: ApiClient, baseUrl = '/api/assistente') {
  const tenant = (tenantId: string) => `tenants/${encodeURIComponent(tenantId)}`
  const root = baseUrl.replace(/\/+$/, '')

  return {
    getMe: (signal?: AbortSignal) => client.get<Me>('me', { signal }),

    listTenants: (search: string | undefined, page: number, signal?: AbortSignal) =>
      client.get<{ items: Tenant[]; totalCount: number; page: number; pageSize: number }>('tenants', {
        query: { search, page, pageSize: 20 },
        signal,
      }),
    getTenant: (tenantId: string, signal?: AbortSignal) => client.get<Tenant>(tenant(tenantId), { signal }),
    updateSettings: (tenantId: string, body: UpdateTenantSettings) => client.put<Tenant>(`${tenant(tenantId)}/settings`, body),
    listRoles: (tenantId: string, signal?: AbortSignal) => client.get<TenantRole[]>(`${tenant(tenantId)}/roles`, { signal }),
    // Rules of a role (not the locked owner); the response lists who is affected.
    updateRole: (tenantId: string, key: string, body: UpdateTenantRole) =>
      client.put<UpdateRoleResult>(`${tenant(tenantId)}/roles/${encodeURIComponent(key)}`, body),
    previewAccess: (tenantId: string, body: AccessPreviewQuery, signal?: AbortSignal) =>
      client.post<AccessPreview>(`${tenant(tenantId)}/access-preview`, body, { signal }),

    listUsers: (tenantId: string, filter: TenantUserFilter, signal?: AbortSignal) =>
      client.get<TenantUserList>(`${tenant(tenantId)}/users`, { query: { ...filter }, signal }),
    getUser: (tenantId: string, userId: string, signal?: AbortSignal) =>
      client.get<TenantUser>(`${tenant(tenantId)}/users/${userId}`, { signal }),
    // Access changes about this person (invitation, role, revocation), from the audit trail.
    userActivity: (tenantId: string, userId: string, signal?: AbortSignal) =>
      client.get<AuditChangeItem[]>(`${tenant(tenantId)}/users/${userId}/activity`, { signal }),
    invite: (tenantId: string, body: InviteUserCommand) =>
      client.post<InviteUserResponse>(`${tenant(tenantId)}/invitations`, body),
    createInvitationLink: (tenantId: string, userId: string) =>
      client.post<InvitationResult>(`${tenant(tenantId)}/users/${userId}/invitations/link`),
    resendInvitation: (tenantId: string, userId: string) =>
      client.post<InvitationResult>(`${tenant(tenantId)}/users/${userId}/invitations`),
    changeAccess: (tenantId: string, userId: string, body: ChangeUserAccessCommand) =>
      client.put<TenantUser>(`${tenant(tenantId)}/users/${userId}/access`, body),
    // WhatsApp / Teams of a person (null keeps, "" removes) and the portal e-mail (only added); also
    // one's own. portalInvitation = the link to create the password, when an e-mail was added.
    changeContacts: (tenantId: string, userId: string, body: ChangeUserContactsCommand) =>
      client.put<ChangeUserContactsResponse>(`${tenant(tenantId)}/users/${userId}/contacts`, body),
    revoke: (tenantId: string, userId: string) => client.post<TenantUser>(`${tenant(tenantId)}/users/${userId}/revoke`),
    deleteUser: (tenantId: string, userId: string) => client.delete(`${tenant(tenantId)}/users/${userId}`),

    getEmailSettings: (tenantId: string, signal?: AbortSignal) =>
      client.get<EmailSettings>(`${tenant(tenantId)}/email-settings`, { signal }),
    updateEmailSettings: (tenantId: string, body: UpdateEmailSettings) =>
      client.put<EmailSettings>(`${tenant(tenantId)}/email-settings`, body),
    deleteEmailSettings: (tenantId: string) => client.delete(`${tenant(tenantId)}/email-settings`),

    // Theme of the portal (Configurações › Marca › Tema do cliente).
    getTheme: (tenantId: string, signal?: AbortSignal) => client.get<TenantTheme>(`${tenant(tenantId)}/theme`, { signal }),
    updateTheme: (tenantId: string, body: UpdateTenantTheme) => client.put<TenantTheme>(`${tenant(tenantId)}/theme`, body),
    // "Gerar cores com IA": colors of the brand identity → suggested light/dark colors (nothing saved).
    suggestThemeColors: (tenantId: string, body: SuggestThemeColors) =>
      client.post<ThemeColorsSuggestion>(`${tenant(tenantId)}/theme/suggest`, body),

    // WhatsApp (Configurações › Canais): the dedicated number's connection and the robot.
    getWhatsAppConnection: (tenantId: string, signal?: AbortSignal) =>
      client.get<WhatsAppConnection>(`${tenant(tenantId)}/whatsapp/connection`, { signal }),
    updateWhatsAppConnection: (tenantId: string, body: UpdateWhatsAppConnection) =>
      client.put<WhatsAppConnection>(`${tenant(tenantId)}/whatsapp/connection`, body),
    testWhatsAppConnection: (tenantId: string) => client.post<WhatsAppTest>(`${tenant(tenantId)}/whatsapp/connection/test`),
    getWhatsAppQrCode: (tenantId: string) => client.get<WhatsAppQrCode>(`${tenant(tenantId)}/whatsapp/connection/qr-code`),
    deleteWhatsAppConnection: (tenantId: string) => client.delete(`${tenant(tenantId)}/whatsapp/connection`),
    getSharedWhatsApp: (tenantId: string, signal?: AbortSignal) => client.get<SharedWhatsApp>(`${tenant(tenantId)}/whatsapp/shared`, { signal }),
    updateAssistant: (tenantId: string, body: AssistantSettings) => client.put<AssistantUpdate>(`${tenant(tenantId)}/assistant`, body),
    uploadAssistantPhoto: (tenantId: string, file: File) => {
      const form = new FormData()
      form.append('file', file)
      return client.put<AssistantUpdate>(`${tenant(tenantId)}/assistant/photo`, form)
    },
    deleteAssistantPhoto: (tenantId: string) => client.delete(`${tenant(tenantId)}/assistant/photo`),
    assistantPhotoUrl: (tenantId: string, updatedAtUtc: string | null | undefined) =>
      updatedAtUtc ? `${root}/public/tenants/${encodeURIComponent(tenantId)}/assistant-photo?v=${encodeURIComponent(updatedAtUtc)}` : null,

    // ERP connection (the system itself is set by the platform); the test records the result.
    testErp: (tenantId: string) => client.post<ErpTestResult>(`${tenant(tenantId)}/erp/test`),
    // A data connector's partition values (customers...): by search, or the names of ids already chosen.
    lookupPartitions: (tenantId: string, query: { search?: string; ids?: string[] }, signal?: AbortSignal) => {
      const params = new URLSearchParams()
      if (query.search) params.set('search', query.search)
      for (const id of query.ids ?? []) params.append('ids', id)
      const qs = params.toString()
      return client.get<DataPartitions>(`${tenant(tenantId)}/data-partitions${qs ? `?${qs}` : ''}`, { signal })
    },

    // Audit trail of access and settings changes (the questions log comes with the assistant).
    listAuditChanges: (tenantId: string, page: number, signal?: AbortSignal) =>
      client.get<AuditChangePage>(`${tenant(tenantId)}/audit/changes`, { query: { page, pageSize: 50 }, signal }),

    // Chat: what the person can ask, their conversations and the question itself (the answer waits
    // for the model and the ERP queries, so it can take several seconds).
    getAssistantProfile: (tenantId: string, viewAsUserId?: string | null, signal?: AbortSignal) =>
      client.get<AssistantProfile>(`${tenant(tenantId)}/assistant/profile`, { query: { viewAsUserId: viewAsUserId || undefined }, signal }),
    listConversations: (tenantId: string, signal?: AbortSignal) =>
      client.get<ConversationSummary[]>(`${tenant(tenantId)}/assistant/conversations`, { signal }),
    getConversation: (tenantId: string, conversationId: string, signal?: AbortSignal) =>
      client.get<Conversation>(`${tenant(tenantId)}/assistant/conversations/${conversationId}`, { signal }),
    listPendingActionPlans: (tenantId: string, signal?: AbortSignal) =>
      client.get<ActionPlanProposal[]>(`${tenant(tenantId)}/assistant/action-plans/pending`, { signal }),
    confirmActionPlan: (tenantId: string, actionPlanId: string) =>
      client.post<ActionPlanProposal>(`${tenant(tenantId)}/assistant/action-plans/${actionPlanId}/confirm`),
    rejectActionPlan: (tenantId: string, actionPlanId: string) =>
      client.post<ActionPlanProposal>(`${tenant(tenantId)}/assistant/action-plans/${actionPlanId}/reject`),
    // The registrations gathered in a conversation (customer, contact) for the "Registro no sistema" panel.
    listActionDrafts: (tenantId: string, conversationId: string, signal?: AbortSignal) =>
      client.get<ActionDraftView[]>(`${tenant(tenantId)}/assistant/conversations/${conversationId}/drafts`, { signal }),
    discardActionDraft: (tenantId: string, conversationId: string, kind: string) =>
      client.delete(`${tenant(tenantId)}/assistant/conversations/${conversationId}/drafts/${kind}`),
    askAssistant: (tenantId: string, question: string, conversationId?: string | null) =>
      client.post<AskAssistantResponse>(`${tenant(tenantId)}/assistant/ask`, { question, conversationId: conversationId ?? null }),
    // Same, streamed (SSE): delta { text }, status { capability }, done (AskAssistantResponse), error { message }.
    askAssistantStream: (
      tenantId: string,
      body: { question: string; conversationId?: string | null; viewAsUserId?: string | null; aiProfile?: string | null },
      options: { signal?: AbortSignal; onEvent: (event: { event: string; data: unknown }) => void },
    ) => client.stream(`${tenant(tenantId)}/assistant/ask/stream`, body, options),

    // Configurações › IA (owners / platform admins): keys are write-only, only their last characters come back.
    getAiSettings: (tenantId: string, signal?: AbortSignal) => client.get<AiSettings>(`${tenant(tenantId)}/ai-settings`, { signal }),
    updateAiDefaults: (tenantId: string, body: UpdateAiDefaults) => client.put<AiSettings>(`${tenant(tenantId)}/ai-settings/defaults`, body),
    saveAiCredential: (tenantId: string, provider: string, apiKey: string) =>
      client.put<AiCredentialSaved>(`${tenant(tenantId)}/ai-settings/credentials/${encodeURIComponent(provider)}`, { apiKey }),
    testAiCredential: (tenantId: string, provider: string) =>
      client.post<AiKeyCheck>(`${tenant(tenantId)}/ai-settings/credentials/${encodeURIComponent(provider)}/test`),
    deleteAiCredential: (tenantId: string, provider: string) =>
      client.delete(`${tenant(tenantId)}/ai-settings/credentials/${encodeURIComponent(provider)}`),
    createAiProfile: (tenantId: string, body: SaveAiProfile) => client.post<AiProfile>(`${tenant(tenantId)}/ai-settings/profiles`, body),
    updateAiProfile: (tenantId: string, key: string, body: SaveAiProfile) =>
      client.put<AiProfile>(`${tenant(tenantId)}/ai-settings/profiles/${encodeURIComponent(key)}`, body),
    deleteAiProfile: (tenantId: string, key: string) => client.delete(`${tenant(tenantId)}/ai-settings/profiles/${encodeURIComponent(key)}`),

    // WhatsApp "Não identificados" (numbers without an active person), owners / platform admins.
    listUnidentifiedContacts: (tenantId: string, signal?: AbortSignal) =>
      client.get<UnidentifiedContact[]>(`${tenant(tenantId)}/whatsapp/unidentified`, { signal }),
    dismissUnidentifiedContact: (tenantId: string, contact: string) =>
      client.post<void>(`${tenant(tenantId)}/whatsapp/unidentified/${encodeURIComponent(contact)}/dismiss`),

    // Conversas (every channel): the person's own, or everyone's for owners / platform admins.
    listTenantConversations: (
      tenantId: string,
      filter: { channel?: string; userId?: string; search?: string; page?: number },
      signal?: AbortSignal,
    ) =>
      client.get<ConversationListPage>(`${tenant(tenantId)}/conversations`, {
        query: {
          channel: filter.channel || undefined,
          userId: filter.userId || undefined,
          search: filter.search || undefined,
          page: filter.page ?? 1,
          pageSize: 30,
        },
        signal,
      }),
    getTenantConversation: (tenantId: string, conversationId: string, signal?: AbortSignal) =>
      client.get<ConversationDetail>(`${tenant(tenantId)}/conversations/${conversationId}`, { signal }),

    // Auditoria › Consultas: the assistant's queries, filtered on the backend (periodDays null = all).
    listAuditQueries: (tenantId: string, filter: AuditQueryFilter, signal?: AbortSignal) =>
      client.get<AuditQueryPage>(`${tenant(tenantId)}/audit/queries`, {
        query: {
          search: filter.search || undefined,
          result: filter.result || undefined,
          channel: filter.channel || undefined,
          capability: filter.capability || undefined,
          periodDays: filter.periodDays ?? undefined,
          all: filter.periodDays === null ? true : undefined,
          pageSize: filter.pageSize ?? 200,
          sort: filter.sort || undefined,
        },
        signal,
      }),

    // Bell: computed notices (invites about to expire, first accesses, ERP); read state per person.
    listNotifications: (tenantId: string, signal?: AbortSignal) =>
      client.get<NotificationList>(`${tenant(tenantId)}/notifications`, { signal }),
    markNotificationsRead: (tenantId: string, ids: string[]) => client.post(`${tenant(tenantId)}/notifications/read`, { ids }),

    // Layout of the tables (columns order and hidden) of the person, per institution; see UiDataTable.
    listTablePreferences: (tenantId: string, signal?: AbortSignal) =>
      client.get<{ items: SavedTableLayout[] }>(`${tenant(tenantId)}/table-preferences`, { signal }).then((result) => result.items),
    saveTablePreference: (tenantId: string, tableId: string, layout: TableLayout) =>
      client.put<SavedTableLayout>(`${tenant(tenantId)}/table-preferences/${encodeURIComponent(tableId)}`, layout),
    deleteTablePreference: (tenantId: string, tableId: string) => client.delete(`${tenant(tenantId)}/table-preferences/${encodeURIComponent(tableId)}`),

    // Addresses: the platform subdomain (acme.aubaine.com.br) and the company's own domains,
    // checked by DNS (TXT + CNAME) before they go live.
    getDomainSettings: (signal?: AbortSignal) => client.get<DomainSettings>('domains/settings', { signal }),
    listDomains: (tenantId: string, signal?: AbortSignal) => client.get<TenantDomain[]>(`${tenant(tenantId)}/domains`, { signal }),
    addDomain: (tenantId: string, host: string) => client.post<TenantDomain>(`${tenant(tenantId)}/domains`, { host }),
    verifyDomain: (tenantId: string, domainId: string) => client.post<TenantDomain>(`${tenant(tenantId)}/domains/${domainId}/verify`),
    setPrimaryDomain: (tenantId: string, domainId: string) => client.put<TenantDomain[]>(`${tenant(tenantId)}/domains/${domainId}/primary`),
    deleteDomain: (tenantId: string, domainId: string) => client.delete(`${tenant(tenantId)}/domains/${domainId}`),

    // Logo (PNG, JPEG or SVG up to 1 MB); a 400 comes with errors.file. Variants: the normal logo, the optional
    // one of the dark theme and the favicon (which may also be an .ico).
    uploadLogo: (tenantId: string, file: File, variant: LogoVariant = 'light') => {
      const form = new FormData()
      form.append('file', file)
      return client.put<Tenant>(`${tenant(tenantId)}/logo${variantQuery(variant, '?')}`, form)
    },
    deleteLogo: (tenantId: string, variant: LogoVariant = 'light') => client.delete(`${tenant(tenantId)}/logo${variantQuery(variant, '?')}`),
    // Public, cacheable logo address; the timestamp changes when the logo is replaced.
    logoUrl: (tenantId: string, updatedAtUtc: string | null | undefined, variant: LogoVariant = 'light') =>
      updatedAtUtc
        ? `${root}/public/tenants/${encodeURIComponent(tenantId)}/logo?${variantQuery(variant, '', '&')}v=${encodeURIComponent(updatedAtUtc)}`
        : null,
    // Square PNG resized by the backend from the favicon (or the logo): 180 = iPhone home screen.
    appIconUrl: (tenantId: string, size: 32 | 180 | 192 | 512, updatedAtUtc: string | null | undefined) =>
      updatedAtUtc
        ? `${root}/public/tenants/${encodeURIComponent(tenantId)}/app-icon?size=${size}&v=${encodeURIComponent(updatedAtUtc)}`
        : null,
    // Web app manifest; `start` is the portal's home as the browser sees it.
    manifestUrl: (tenantId: string, start: string) =>
      `${root}/public/tenants/${encodeURIComponent(tenantId)}/manifest.webmanifest?start=${encodeURIComponent(start)}`,
  }
}

export type PortalApi = ReturnType<typeof createPortalApi>

// Filters of Auditoria › Consultas ("" = all). periodDays: 0 (today), 7, 30 or null (whole window).
export interface AuditQueryFilter {
  search?: string
  result?: string
  channel?: string
  capability?: string
  periodDays: number | null
  pageSize?: number
  // "occurredAtUtc,-userName" (comma-separated item fields, '-' = descending); see sortParam of @acme/ui.
  sort?: string
}

export const PORTAL_API: InjectionKey<PortalApi> = Symbol('portal-api')

export function usePortalApi(): PortalApi {
  const api = inject(PORTAL_API)
  if (!api) {
    throw new Error('usePortalApi() needs the portal API provided by the app.')
  }
  return api
}

export const queryKeys = {
  me: ['me'] as const,
  tenants: (search: string | undefined, page: number) => ['tenants', search ?? '', page] as const,
  tenant: (tenantId: string) => ['tenant', tenantId] as const,
  roles: (tenantId: string) => ['tenant', tenantId, 'roles'] as const,
  aiSettings: (tenantId: string) => ['tenant', tenantId, 'ai-settings'] as const,
  users: (tenantId: string) => ['tenant', tenantId, 'users'] as const,
  user: (tenantId: string, userId: string) => ['tenant', tenantId, 'users', userId] as const,
  userActivity: (tenantId: string, userId: string) => ['tenant', tenantId, 'users', userId, 'activity'] as const,
  audit: (tenantId: string) => ['tenant', tenantId, 'audit'] as const,
  assistant: (tenantId: string) => ['tenant', tenantId, 'assistant'] as const,
  conversations: (tenantId: string) => ['tenant', tenantId, 'conversations'] as const,
  notifications: (tenantId: string) => ['tenant', tenantId, 'notifications'] as const,
  emailSettings: (tenantId: string) => ['tenant', tenantId, 'email-settings'] as const,
  theme: (tenantId: string) => ['tenant', tenantId, 'theme'] as const,
  whatsApp: (tenantId: string) => ['tenant', tenantId, 'whatsapp'] as const,
  sharedWhatsApp: (tenantId: string) => ['tenant', tenantId, 'whatsapp-shared'] as const,
  domainSettings: ['domain-settings'] as const,
  domains: (tenantId: string) => ['tenant', tenantId, 'domains'] as const,
  preview: (tenantId: string, role: string, units: string, draft = '') => ['tenant', tenantId, 'preview', role, units, draft] as const,
}
